import {
  createAttachment,
  createAttachmentsList,
  createLessonPlan,
  createLongCardListType,
  createNoteNode,
} from '../nodeCreationHelpers'
import { ISimplifiedNode } from '../../components/ingestion/IngestedTree'
import { IMaterial, IModuleParams } from './types'
import { getModuleLessonCount } from './moduleUtility'
import { createTable } from '../html_utility'
import { getTeacherBackgroundKnowledge } from './teacherBackgroundKnowledge'
import {
  GuidanceForPersonalGlossaryContent,
  GuidanceForPersonalGlossaryHeaderRow,
  GuidanceForPersonalGlossaryLessonRow,
  GuidanceForPersonalGlossaryTableEnd,
  GuidanceForPersonalGlossaryTableStart,
} from './resources'

export function createTeacherReferenceMaterialsSection(params: IModuleParams) {
  return createLongCardListType({
    title: 'Teacher Reference Materials',
    nodes: [
      createStudentHandouts(params),
      getAssessmentOverview(params),
      createGuidanceForPersonalGlossary(params),
      ...createMaterials(params),
      createHomeCommunication(),
    ],
  })
}

function gDriveUnitAssessmentDoc(params: IModuleParams) {
  const key = `${params.metadata.courseCode.split('/').pop()}ASMT`
  if (key in params.gDrive) {
    return params.gDrive[key]
  }
}

function getAssessmentOverview(params: IModuleParams) {
  return createLessonPlan(
    {
      title: 'Unit Assessment Overview',
    },
    [
      createNoteNode({
        title: 'Assessment System Overview',
        studentFacing: false,
        value:
          'Each OpenSciEd unit includes an assessment system that offers many opportunities for different types of assessments throughout the lessons, including pre-assessment, formative assessment, summative assessment, and student self assessment. Formative assessments are embedded and called out directly in the lesson plans. Please look for the “Assessment Icon” in the teacher support boxes to identify places for assessments. In addition, the table below outlines where each type of assessment can be found in the unit.',
      }),
      ...assessmentTableNode(params),
    ],
  )
}

function assessmentTableNode(params: IModuleParams) {
  const table = createMaterialTable(params)
  if (!table) return []
  return [
    createNoteNode({
      title: 'Overall Unit Assessment',
      studentFacing: false,
      value: table,
    }),
  ]
}

function createMaterialTable(params: IModuleParams) {
  const docData = gDriveUnitAssessmentDoc(params)
  const sourceContent = docData?.objectContent['[assessment-overview]']
  if (!sourceContent) return
  const { headers, data } = sourceContent

  const rows = data.reduce(
    (acc, rowData) => {
      return [
        ...acc,
        headers.map((key) => {
          return rowData[key]
        }),
      ]
    },
    [headers],
  )

  return createTable(rows, {
    cellStyle(rowIndex, cellIndex) {
      let style =
        'vertical-align: top; padding: 5pt 5pt 5pt 5pt; overflow: hidden; overflow-wrap: break-word; border: solid #000000 1pt;'
      if (cellIndex === 0) {
        style += 'width: 97px;'
      } else if (cellIndex === 1) {
        style += 'width: 126px;'
      }
      return { style }
    },
  })
}

function createMaterials(params: IModuleParams): ISimplifiedNode[] {
  return params.metadata.material
    .filter((m) => m['@id'] !== 'unit-storyline')
    .map((m) => parseMaterialItem(m, params))
}

export function createMaterialAttachment(m: IMaterial) {
  return createAttachmentsList({
    title: 'See Full PDF for strategies and adult-level learning resources.',
    attachments_list: [createAttachment({ ...m, url: m.url! })],
  })
}

export function parseMaterialItem(m: IMaterial, params: IModuleParams) {
  const nodes: ISimplifiedNode[] = [
    ...getExtraContent(m, params),
    createMaterialAttachment(m),
  ]
  return createLessonPlan({ title: m.name }, nodes)
}

function getExtraContent(m: IMaterial, params: IModuleParams) {
  if (m['@id'] !== 'background') return []
  return getTeacherBackgroundKnowledge(params)
}

function createStudentHandouts(params: IModuleParams) {
  const lessonCount = getModuleLessonCount(params)
  const attachmentsList = Array.from(Array(lessonCount).keys()).map((index) => {
    return createAttachmentsList({
      title: `Lesson ${index + 1}`,
      attachments_list: [],
    })
  })
  return createLessonPlan(
    {
      title: 'Student Handouts and Teacher Reference Materials',
    },
    attachmentsList,
  )
}

function createHomeCommunication() {
  return createLessonPlan(
    {
      title: 'Home Communication',
    },
    [
      createNoteNode({
        title: 'ID note',
        value: '',
        studentFacing: false,
      }),
      createAttachmentsList({
        title: 'Home Communication - English',
        attachments_list: [],
      }),
      createAttachmentsList({
        title: 'Home Communication - Spanish',
        attachments_list: [],
      }),
    ],
  )
}

function createGuidanceForPersonalGlossary(params: IModuleParams) {
  return createLessonPlan(
    {
      title: 'Guidance for Developing Your Personal Glossary',
    },
    [
      createNoteNode({
        title: 'Guidance for Developing Your Personal Glossary',
        value: GuidanceForPersonalGlossaryContent + lessonsTable(params),
        studentFacing: false,
      }),
    ],
  )
}

function lessonsTable(params: IModuleParams) {
  const lessonCount = getModuleLessonCount(params)

  const lessonRows = Array.from({ length: lessonCount }).map((_, index) => {
    return GuidanceForPersonalGlossaryLessonRow.replace(
      'LESSON_NUM',
      'L' + (index + 1),
    )
  })

  return [
    GuidanceForPersonalGlossaryTableStart,
    GuidanceForPersonalGlossaryHeaderRow,
    ...lessonRows,
    GuidanceForPersonalGlossaryTableEnd,
  ].join('')
}
